import appConfigs from "@configs/app";
import { userGoogleCredential } from "@plugins/google/user.google.credential";
import { googleAuth } from "@plugins/google/google.auth";
import { isEmpty, isDefined } from "@utils/var.check";
import {
  PICKER_ERROR,
  ALLOWED_MIMETYPES,
  CONFIG,
  ATTEMPT_ALLOWED_MIMETYPES,
  TYPE_CONTEXT,
} from "@plugins/google/google.constant";
import type { UserGoogleCredentialData } from "@plugins/google/types";

interface GooglePickerOptions {
  pickerCallback: (data: unknown) => void;
}

export default class GooglePicker {
  pickerApiLoaded = false;
  accessCode;
  userCredential: UserGoogleCredentialData = {
    userLinkedEmail: "",
    accessToken: "",
    isAccessTokenExpired: false,
  };
  picker = null;
  pickerCallback: (data: unknown) => void;

  constructor(options: GooglePickerOptions) {
    this.pickerCallback = options.pickerCallback;
  }

  async loadPicker(config: string) {
    await userGoogleCredential.fetchLinkedAccountCredential();
    this.userCredential = userGoogleCredential.getCredential();
    if (isEmpty(this.userCredential.userLinkedEmail)) {
      return Promise.reject(PICKER_ERROR.EMPTY_EMAIL);
    }

    try {
      if (this.userCredential.isAccessTokenExpired) {
        await userGoogleCredential.refreshLinkedAccountCredential();
        this.userCredential = userGoogleCredential.getCredential();
      }
      await googleAuth.checkAuthInstanceInitState();

      if (googleAuth.requireSignIn) {
        googleAuth.handleRequestCode();
        return;
      }

      googleAuth.checkUserCodeExpire().then(() => {
        this.openPicker(config).then(() => {
          return Promise.resolve();
        });
      });
    } catch (error) {
      return Promise.reject(error);
    }
    return Promise.resolve();
  }

  async openPicker(config: string) {
    try {
      if (!this.pickerApiLoaded) {
        await this.loadScript();

        // load picker api
        await new Promise<void>((resolve) => {
          window.gapi.load("picker", () => {
            this.pickerApiLoaded = true;
            resolve();
          });
        });
      }

      this.createPicker(config);
    } catch (err) {
      console.error("Failed to open picker:", err);
      throw err;
    }
  }

  async loadScript() {
    if (isDefined(window.gapi)) {
      return;
    }

    const scriptConfig = appConfigs.googleApiLibraryScript;
    const scriptEl = document.createElement("script");

    for (const key in scriptConfig) {
      if (scriptConfig[key]) {
        scriptEl.setAttribute(key, scriptConfig[key]);
      }
    }

    await new Promise((resolve, reject) => {
      scriptEl.onload = resolve;
      scriptEl.onerror = reject;
      document.head.appendChild(scriptEl);
    });
  }

  createPicker(config: string) {
    const oAuthToken = this.userCredential.accessToken;
    if (this.pickerApiLoaded && oAuthToken) {
      const docsView = new window.google.picker.DocsView().setOwnedByMe(true);
      const sharedView = new window.google.picker.DocsView(
        window.google.picker.ViewId.DOCS,
      ).setEnableDrives(true);
      const uploadView = new window.google.picker.DocsUploadView();
      const sharedWithMeView = new window.google.picker.DocsView().setOwnedByMe(false);

      // Only allow certain file types (see SLS-8289) for embedding
      if (config === CONFIG.EMBED) {
        docsView.setMimeTypes(ALLOWED_MIMETYPES);
        sharedView.setMimeTypes(ALLOWED_MIMETYPES);
        uploadView.setMimeTypes(ALLOWED_MIMETYPES);
        sharedWithMeView.setMimeTypes(ALLOWED_MIMETYPES);
      } else if (config === TYPE_CONTEXT.FRQ || config === TYPE_CONTEXT.TEACHER_FEEDBACK) {
        docsView.setMimeTypes(ATTEMPT_ALLOWED_MIMETYPES);
        sharedView.setMimeTypes(ATTEMPT_ALLOWED_MIMETYPES);
        uploadView.setMimeTypes(ATTEMPT_ALLOWED_MIMETYPES);
        sharedWithMeView.setMimeTypes(ATTEMPT_ALLOWED_MIMETYPES);
      }

      const picker = new window.google.picker.PickerBuilder()
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .setAppId(googleAuth.googleClientCredential!.projectNo)
        .setOAuthToken(oAuthToken)
        .addView(docsView)
        .addView(sharedWithMeView)
        .addView(uploadView)
        .addView(sharedView)
        .setDeveloperKey(googleAuth.googleClientCredential!.developerKey)
        .setCallback(this.pickerCallback)
        .build();
      picker.setVisible(true);
      this.picker = picker;
    }
  }

  closePicker() {
    if (this.picker && this.picker.isVisible()) {
      this.picker.dispose();
    }
  }
}
